import React, { useState } from 'react'

import cn from 'classnames'
import Skeleton from 'react-loading-skeleton'

import ChevronLeftIcon from '@src/components/icons/ChevronLeft'
import ChevronRightIcon from '@src/components/icons/ChevronRight'
import { ILeaderboardDataResponse } from '@src/types/point.api'
import { truncateAddress } from '@src/utils/truncateAddress'

type Props = {
    account?: string | null
    loading: boolean
    leaderboard: ILeaderboardDataResponse[]
}

export const Leaderboard: React.FC<Props> = ({ account, loading, leaderboard }) => {
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 10

    // Sort leaderboard by points (highest first) and add rankings
    const sortedLeaderboard = leaderboard
        .sort((a, b) => b.total_user_points - a.total_user_points)
        .map((item, index) => ({
            ...item,
            rank: index + 1,
        }))

    // Calculate pagination
    const totalPages = Math.max(1, Math.ceil(sortedLeaderboard.length / itemsPerPage))
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentPageData = sortedLeaderboard.slice(startIndex, endIndex)

    // Loading skeleton
    const renderLoading = () => (
        <div className='flex w-full flex-col gap-2'>
            {Array(10)
                .fill(0)
                .map((_, index) => (
                    <div key={index} className='flex h-5 w-full items-center gap-2.5 sm:w-[400px]'>
                        <div className='w-[60px]'>
                            <Skeleton enableAnimation baseColor='#9B9B9B' className='h-4 w-6' />
                        </div>
                        <div className='flex-1'>
                            <Skeleton enableAnimation baseColor='#9B9B9B' className='h-4 w-24' />
                        </div>
                        <div className='w-[100px] text-right'>
                            <Skeleton enableAnimation baseColor='#9B9B9B' className='h-4 w-16' />
                        </div>
                    </div>
                ))}
        </div>
    )

    return (
        <div className='flex flex-col items-center gap-4'>
            {/* Header */}
            <div className='flex h-5 w-full items-center gap-1 sm:w-[400px] sm:gap-2.5'>
                <div className='w-[60px] text-sm font-medium leading-5 text-[#757575]'>#</div>
                <div className='flex-1 text-sm font-medium leading-5 text-[#757575]'>Wallet</div>
                <div className='text-right text-sm font-medium leading-5 text-[#757575]'>Miles</div>
            </div>

            {/* Leaderboard Items */}
            {loading ? (
                renderLoading()
            ) : currentPageData.length > 0 ? (
                <div className='flex w-full flex-col gap-1 sm:w-[400px] sm:gap-2'>
                    {currentPageData.map(item => (
                        <div key={item.wallet} className='flex h-5 w-full items-center gap-1 sm:w-[400px] sm:gap-2.5'>
                            <div className='w-[60px] text-sm font-medium leading-5 text-white'>{item.rank}</div>
                            <div className='flex-1 text-sm font-medium leading-5 text-white'>
                                {truncateAddress(item.wallet, {
                                    evmPrefixLen: 6,
                                    evmSuffixLen: 4,
                                    solanaPrefixLen: 6,
                                    solanaSuffixLen: 4,
                                    tronPrefixLen: 6,
                                    tronSuffixLen: 4,
                                })}
                            </div>
                            <div className='w-[100px] text-right text-sm font-medium leading-5 text-white'>
                                {item.total_user_points.toLocaleString()}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className='flex h-20 w-[400px] items-center justify-center text-sm text-[#757575]'>
                    No leaderboard data available
                </div>
            )}

            {/* Pagination - show loading skeleton or actual pagination */}
            {(loading || (!loading && sortedLeaderboard.length > 0 && totalPages > 1)) && (
                <div className='flex w-full flex-col items-center gap-2.5'>
                    {loading ? (
                        // Loading skeleton for pagination
                        <div className='flex h-9 gap-4'>
                            <div className='h-9 w-9 rounded-lg'>
                                <Skeleton enableAnimation baseColor='#9B9B9B' className='h-9 w-9' />
                            </div>
                            <div className='m-auto w-[120px] text-center'>
                                <Skeleton enableAnimation baseColor='#9B9B9B' className='h-5 w-20' />
                            </div>
                            <div className='h-9 w-9 rounded-lg'>
                                <Skeleton enableAnimation baseColor='#9B9B9B' className='h-9 w-9' />
                            </div>
                        </div>
                    ) : (
                        // Actual pagination controls
                        <div className='flex items-center gap-4'>
                            <button
                                className={cn(
                                    'flex items-center gap-1.5 rounded-lg bg-[#212326] p-2 transition-colors',
                                    currentPage === 1 ? 'cursor-not-allowed opacity-50' : 'hover:bg-[#2A2D33]'
                                )}
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                            >
                                <ChevronLeftIcon className='h-5 w-5' />
                            </button>
                            <div className='w-[120px] text-center'>
                                <span className='text-sm font-medium leading-5 text-[#757575]'>Page </span>
                                <span className='text-sm font-medium leading-5 text-white'>{currentPage}</span>
                                <span className='text-sm font-medium leading-5 text-[#757575]'> of </span>
                                <span className='text-sm font-medium leading-5 text-white'>{totalPages}</span>
                            </div>
                            <button
                                className={cn(
                                    'flex items-center gap-1.5 rounded-lg bg-[#212326] p-2 transition-colors',
                                    currentPage === totalPages ? 'cursor-not-allowed opacity-50' : 'hover:bg-[#2A2D33]'
                                )}
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                            >
                                <ChevronRightIcon className='h-5 w-5' />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
